import React from 'react';
import { observer } from 'mobx-react';
import ReactTooltip from 'react-tooltip';
import '../../../css/ExpandableStandardsScoringTable.scss';
import { register } from '../../../i18n';
import HtmlComponent from '../HtmlComponent';

const t = register('TeacherAIScoreResultsPanel');

/**
 * **ExpandableStandardsScoringTable:** Displays standards data in collapsed or expanded mode
 *
 * **Collapsed mode:** Standard Code | Mastery
 * **Expanded mode:** Standard (code + description) | Mastery | Justification | Standard ID (optional)
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.standards - Array of standard objects with standardCode, description, mastery, justification, standardID
 * @param {boolean} [props.expanded=false] - Whether to show expanded view with description and justification columns
 * @param {boolean} [props.showStandardId=false] - Whether to show standard ID column in expanded mode
 * @returns {React.Component|null} The standards scoring table component or null if no standards
 */
const ExpandableStandardsScoringTable = observer(({
  standards = [],
  expanded = false,
  showStandardId = false
} = {}) => {
  if (!standards || standards.length === 0) {
    return null;
  }

  // Collapsed mode - Standard (with description tooltip) | Mastery
  if (!expanded) {
    return (
      <>
        <table aria-label={t('attachedStandards')} className='expandable-standards-scoring-table collapsed'>
          <thead>
            <tr>
              <th className='standard-header'>{t('standardCode')}</th>
              <th className='mastery-header'>{t('mastery')}</th>
            </tr>
          </thead>
          <tbody>
            {standards.map((standard, index) => {
              // Create tooltip content with standard code + description
              const tooltipContent = standard.description ? (
                `<strong>${standard.standardCode}</strong> ${standard.description}`
              ) : standard.standardCode;

              return (
                <tr key={index}>
                  <td className='standard-code'>
                    <div className='standard-code-text' data-tip={tooltipContent}>
                      {standard.standardCode}
                    </div>
                  </td>
                  <td className='mastery'>{standard.mastery}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <ReactTooltip border className='react-tooltip expandable-standards-scoring-table__default-tooltip' delayShow={500}
          effect='solid' html={true} place='top' type='light' />
      </>
    );
  }

  // Expanded mode - Standard | Mastery | Justification | Standard ID (optional)
  return (
    <table aria-label={t('attachedStandards')} className='expandable-standards-scoring-table expanded'>
      <thead>
        <tr>
          <th className='standard-header'>{t('standard')}</th>
          <th className='mastery-header'>{t('mastery')}</th>
          <th className='justification-header'>{t('justification')}</th>
          {showStandardId && (
            <th className='standard-id-header'>{t('standardId')}</th>
          )}
        </tr>
      </thead>
      <tbody>
        {standards.map((standard, index) => (
          <tr key={index}>
            <td className='standard-with-description'>
              <span className='standard-code-text'>{standard.standardCode}</span>
              {standard.description && (
                <HtmlComponent className='standard-description' htmlStr={standard.description} useSpan={true} />
              )}
            </td>
            <td className='mastery'>{standard.mastery}</td>
            <td className='justification'>{standard.justification}</td>
            {showStandardId && (
              <td className='standard-id'>{standard.standardID}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
});

export default ExpandableStandardsScoringTable;
